// b16: den förseglade lådan. En hemlighet som ägaren skriver in i appen, till exempel nyckeln för
// en Victron-enhet över Bluetooth, krypteras i webbläsaren med navets publika nyckel. Molnet ser
// bara lådan och kan inte öppna den: bara navet har den privata nyckeln, och den lämnar aldrig
// navet. Molnet tar bort lådan när navet har tagit emot den.
//
// ECDH med P-256 och en tillfällig nyckel per låda, HKDF-SHA256 och AES-256-GCM, med WebCrypto,
// som finns både i webbläsaren och i Node. Lådan är bunden till sitt ämne (till exempel fyndets
// id): navet öppnar den bara för samma ämne.

export interface Forseglad {
  v: 1;
  /** Den tillfälliga publika nyckeln, rå (65 byte), base64url. */
  epk: string;
  /** AES-GCM:s iv, 12 byte, base64url. */
  iv: string;
  /** Det krypterade, med GCM:s tagg, base64url. */
  ct: string;
}

const INFO = "koll-lada-v1";
const B64URL = /^[A-Za-z0-9_-]+$/;

/** Navets publika nyckel: P-256, rå och okomprimerad (65 byte), base64url. */
export const LADA_PUBLIK = /^[A-Za-z0-9_-]{87}$/;

function crypto_(): SubtleCrypto {
  const s = globalThis.crypto?.subtle;
  if (!s) throw new Error("Krypteringen finns inte här (sidan behöver https)");
  return s;
}

export function tillBase64url(b: Uint8Array): string {
  let s = "";
  for (const x of b) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function franBase64url(t: string): Uint8Array<ArrayBuffer> {
  if (!B64URL.test(t)) throw new Error("inte base64url");
  const s = atob(t.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((t.length + 3) % 4));
  const ut = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) ut[i] = s.charCodeAt(i);
  return ut;
}

const text = (t: string) => new TextEncoder().encode(t);

async function aesNyckel(
  delad: ArrayBuffer,
  epk: Uint8Array<ArrayBuffer>,
  mottagare: Uint8Array<ArrayBuffer>,
): Promise<CryptoKey> {
  const s = crypto_();
  const bas = await s.importKey("raw", delad, "HKDF", false, ["deriveKey"]);
  const salt = new Uint8Array(epk.length + mottagare.length);
  salt.set(epk);
  salt.set(mottagare, epk.length);
  return s.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt, info: text(INFO) },
    bas,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

const aad = (amne: string) => text(`${INFO}\n${amne}`);

/** Kontrollerar formen på en låda. Returnerar ett fel, eller null. */
export function kontrolleraForseglad(v: unknown): string | null {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return "lådan saknas";
  const x = v as Record<string, unknown>;
  if (Object.keys(x).some((k) => !["v", "epk", "iv", "ct"].includes(k))) return "okänt fält";
  if (x.v !== 1) return "okänd version";
  if (typeof x.epk !== "string" || !LADA_PUBLIK.test(x.epk)) return "nyckeln i lådan";
  if (typeof x.iv !== "string" || !/^[A-Za-z0-9_-]{16}$/.test(x.iv)) return "lådans iv";
  if (typeof x.ct !== "string" || !B64URL.test(x.ct) || x.ct.length < 22 || x.ct.length > 1400) {
    return "lådans innehåll";
  }
  return null;
}

/**
 * Förseglar en text till navet med dess publika nyckel. Ämnet (till exempel fyndets id) binds
 * till lådan, så att den inte kan användas för något annat.
 */
export async function forsegla(publik: string, amne: string, innehall: string): Promise<Forseglad> {
  if (!LADA_PUBLIK.test(publik)) throw new Error("Navets nyckel för lådan har fel form");
  const s = crypto_();
  const mottagare = franBase64url(publik);
  const mottagarNyckel = await s.importKey(
    "raw",
    mottagare,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    [],
  );
  const tillfallig = (await s.generateKey({ name: "ECDH", namedCurve: "P-256" }, true, [
    "deriveBits",
  ])) as CryptoKeyPair;
  const epk = new Uint8Array(await s.exportKey("raw", tillfallig.publicKey));
  const delad = await s.deriveBits(
    { name: "ECDH", public: mottagarNyckel },
    tillfallig.privateKey,
    256,
  );
  const nyckel = await aesNyckel(delad, epk, mottagare);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(
    await s.encrypt({ name: "AES-GCM", iv, additionalData: aad(amne) }, nyckel, text(innehall)),
  );
  return { v: 1, epk: tillBase64url(epk), iv: tillBase64url(iv), ct: tillBase64url(ct) };
}

/** Navets nyckelpar för lådan. Den privata nyckeln lämnar aldrig navet. */
export async function nyttLadpar(): Promise<{ publik: string; privat: string }> {
  const s = crypto_();
  const par = (await s.generateKey({ name: "ECDH", namedCurve: "P-256" }, true, [
    "deriveBits",
  ])) as CryptoKeyPair;
  return {
    publik: tillBase64url(new Uint8Array(await s.exportKey("raw", par.publicKey))),
    privat: tillBase64url(new Uint8Array(await s.exportKey("pkcs8", par.privateKey))),
  };
}

/**
 * Öppnar en låda med navets nycklar. Kastar ett fel om lådan inte är till det här navet, har
 * ändrats eller gäller ett annat ämne.
 */
export async function oppna(
  par: { publik: string; privat: string },
  amne: string,
  l: Forseglad,
): Promise<string> {
  const fel = kontrolleraForseglad(l);
  if (fel) throw new Error(`Lådan har fel form: ${fel}`);
  const s = crypto_();
  const privat = await s.importKey(
    "pkcs8",
    franBase64url(par.privat),
    { name: "ECDH", namedCurve: "P-256" },
    false,
    ["deriveBits"],
  );
  const epk = franBase64url(l.epk);
  const avsandare = await s.importKey("raw", epk, { name: "ECDH", namedCurve: "P-256" }, false, []);
  const delad = await s.deriveBits({ name: "ECDH", public: avsandare }, privat, 256);
  const nyckel = await aesNyckel(delad, epk, franBase64url(par.publik));
  const klar = await s.decrypt(
    { name: "AES-GCM", iv: franBase64url(l.iv), additionalData: aad(amne) },
    nyckel,
    franBase64url(l.ct),
  );
  return new TextDecoder().decode(klar);
}
